import { validateFieldValue, type FieldType } from "@/lib/crm-fields/validate";
import { subtreeIds, MAX_DEPTH, depthOf } from "@/lib/crm-fields/tag-tree";
import type { CustomFieldDef, EntityType, Tag } from "./types";

export interface Backend {
  org: string;
  findDef(entity_type: EntityType, field_key: string): Promise<CustomFieldDef | null>;
  upsertDef(def: CustomFieldDef): Promise<void>;
  getValue(field_id: string, entity_id: string): Promise<unknown>;
  setValue(field_id: string, entity_id: string, value: unknown): Promise<void>;
  findTag(slug: string, parent_id: string | null): Promise<Tag | null>;
  listTags(): Promise<Tag[]>;
  upsertTag(tag: Tag): Promise<void>;
  hasTagging(tag_id: string, entity_type: EntityType, entity_id: string): Promise<boolean>;
  addTagging(tag_id: string, entity_type: EntityType, entity_id: string): Promise<void>;
  removeTagging(tag_id: string, entity_type: EntityType, entity_id: string): Promise<void>;
}

const uid = () => (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));
const now = () => new Date().toISOString();

export function makeContracts(be: Backend) {
  async function upsertFieldDef(
    entity_type: EntityType, field_key: string,
    spec: { label: string; type: FieldType; options?: string[]; required?: boolean },
  ): Promise<CustomFieldDef> {
    const existing = await be.findDef(entity_type, field_key);
    const def: CustomFieldDef = existing ?? {
      id: uid(), organization_id: be.org, entity_type, field_key,
      label: spec.label, field_type: spec.type, options: spec.options ?? [],
      is_required: !!spec.required, sort_order: 0, archived: false,
      created_at: now(), updated_at: now(),
    };
    if (existing) {
      def.label = spec.label; def.field_type = spec.type;
      def.options = spec.options ?? []; def.is_required = !!spec.required;
      def.updated_at = now();
    }
    await be.upsertDef(def);
    return def;
  }

  async function setValue(entity_type: EntityType, entity_id: string, field_key: string, value: unknown) {
    const def = await be.findDef(entity_type, field_key);
    if (!def) throw new Error(`no such field: ${entity_type}.${field_key}`);
    const v = validateFieldValue(def.field_type, value, def.options);
    if (!v.ok) throw new Error(`${field_key}: ${v.error}`);
    await be.setValue(def.id, entity_id, v.value);
  }
  async function getValue(entity_type: EntityType, entity_id: string, field_key: string) {
    const def = await be.findDef(entity_type, field_key);
    if (!def) return null;
    return be.getValue(def.id, entity_id);
  }

  async function ensureTag(slug: string, opts: { name: string; parent_slug?: string }): Promise<Tag> {
    let parent_id: string | null = null;
    if (opts.parent_slug) {
      const parent = await be.findTag(opts.parent_slug, null)
                  ?? await ensureTag(opts.parent_slug, { name: opts.parent_slug });
      parent_id = parent.id;
    }
    const existing = await be.findTag(slug, parent_id);
    if (existing) return existing;
    const all = await be.listTags();
    const proposedDepth = parent_id
      ? depthOf(parent_id, all.map(t => ({ id: t.id, parent_id: t.parent_id }))) + 1
      : 1;
    if (proposedDepth > MAX_DEPTH) throw new Error(`tag depth > ${MAX_DEPTH}`);
    const tag: Tag = { id: uid(), organization_id: be.org, name: opts.name, slug,
                       color: null, parent_id, archived: false, created_at: now(), updated_at: now() };
    await be.upsertTag(tag);
    return tag;
  }

  /** Returns true when a tagging was actually added (false = already had it). */
  async function applyTag(slug: string, entity_type: EntityType, entity_id: string): Promise<boolean> {
    const tag = await be.findTag(slug, null) ?? await findAnywhere(slug);
    if (!tag) throw new Error(`tag not found: ${slug}`);
    if (await be.hasTagging(tag.id, entity_type, entity_id)) return false;
    await be.addTagging(tag.id, entity_type, entity_id);
    return true;
  }
  async function removeTag(slug: string, entity_type: EntityType, entity_id: string) {
    const tag = await findAnywhere(slug); if (!tag) return;
    await be.removeTagging(tag.id, entity_type, entity_id);
  }
  async function hasTag(entity_type: EntityType, entity_id: string, slug: string,
                        opts: { includeDescendants?: boolean } = {}) {
    const tag = await findAnywhere(slug); if (!tag) return false;
    if (!opts.includeDescendants) {
      return be.hasTagging(tag.id, entity_type, entity_id);
    }
    const all = await be.listTags();
    const ids = subtreeIds(tag.id, all.map(t => ({ id: t.id, parent_id: t.parent_id })));
    for (const id of ids) if (await be.hasTagging(id, entity_type, entity_id)) return true;
    return false;
  }

  async function findAnywhere(slug: string): Promise<Tag | null> {
    const all = await be.listTags();
    return all.find(t => t.slug === slug) ?? null;
  }

  return { upsertFieldDef, setValue, getValue, ensureTag, applyTag, removeTag, hasTag };
}
